import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { Check, X, Edit2, Save, Info, ExternalLink } from "lucide-react";
import LoadingBlack from "../../images/loading-black.svg";
import { useNavigate } from "react-router-dom";

function RoleMatchRequestModal({ isOpen, onClose, candidate, selectedRole, onRequestSent }) {
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [roleDetails, setRoleDetails] = useState(null);
  const [error, setError] = useState(null);
  const [availableRoles, setAvailableRoles] = useState([]);
  const navigate = useNavigate();
  const [isRoleInfoMissing, setIsRoleInfoMissing] = useState(false);
  const [completionPercentage, setCompletionPercentage] = useState(0);
  
  // Form state
  const [formData, setFormData] = useState({
    hiringLeadEmail: "",
    hiringLeadName: "",
    eyeCatchingInfo: ""
  });
  
  // Role editing state
  const [editMode, setEditMode] = useState({
    compensation: false,
    workingStyle: false,
    jobDescription: false,
    roleTitle: false,
    jobDescriptionPDF: false
  });
  
  // Editable role details state
  const [editableRoleDetails, setEditableRoleDetails] = useState({
    "Compensation": "",
    "Working Style": "",
    "Job Description": "",
    "Role Title": "",
    "Job Description PDF": ""
  });
  
  // Clear state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setRoleDetails(null);
      setShowSuccess(false);
      setError(null);
      setFormData({
        hiringLeadEmail: "",
        hiringLeadName: "",
        eyeCatchingInfo: ""
      });
    } else {
      // Fetch available roles when the modal opens
      fetchAvailableRoles();
    }
  }, [isOpen]);

  // Fetch available roles to find a placeholder role or use as fallback
  const fetchAvailableRoles = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get("/api/roles/fetchUserRoles", {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      
      setAvailableRoles(response.data.roles || []);
    } catch (error) {
      console.error("Error fetching roles:", error);
    }
  };

  // Fetch role details when a role is selected
  useEffect(() => {
    if (isOpen && selectedRole) {
      fetchRoleDetails(selectedRole.id);
    }
  }, [isOpen, selectedRole]);

  // Update useEffect to set the completion percentage
  useEffect(() => {
    if (roleDetails) {
      // Use the enhanced isRoleComplete function
      const roleCompletion = isRoleComplete(roleDetails);
      setIsRoleInfoMissing(!roleCompletion.isComplete);
      setCompletionPercentage(roleCompletion.completionPercentage);
      
      // Log for debugging
      console.log("Role details changed:", {
        compensation: roleDetails["Compensation"],
        workingStyle: roleDetails["Working Style"],
        jobDescription: roleDetails["Job Description"],
        completionResult: roleCompletion,
        isRoleInfoMissing: !roleCompletion.isComplete
      });
    }
  }, [roleDetails]);

  const fetchRoleDetails = async (roleId) => {
    setLoading(true);
    
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`/api/roles/fetchRoleData?id=${roleId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      
      // When using fetchRoleData endpoint, the data structure is different
      // The API response has fields directly in the response.data.role.fields
      const roleData = response.data.role.fields || {};
      
      console.log("Role data retrieved:", roleData);
      
      setRoleDetails(roleData);
      
      // Initialize editable role details with correct field names
      setEditableRoleDetails({
        "Compensation": roleData["Compensation"] || "",
        "Working Style": roleData["Working Style"] || "",
        "Job Description": roleData["Job Description"] || "",
        "Role Title": roleData["Role Title"] || "",
        "Job Description PDF": roleData["Job Description PDF"] || ""
      });
      
      // Pre-fill form data if available
      setFormData({
        hiringLeadEmail: roleData["Hiring Lead Email Address"] || "",
        hiringLeadName: roleData["Hiring Lead Name"] || "",
        eyeCatchingInfo: ""
      });
    } catch (error) {
      console.error("Error fetching role details:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle changes to editable role details
  const handleRoleDetailChange = (field, value) => {
    setEditableRoleDetails(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  // Save edited role details
  const saveRoleDetails = async (field) => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem("token");
      
      // Create update data object with only the field being edited
      const updateData = {
        fields: {},
        roleId: selectedRole.id
      };
      
      // Add the appropriate field to the update
      switch (field) {
        case 'compensation':
          updateData.fields["Compensation"] = editableRoleDetails["Compensation"];
          break;
        case 'workingStyle':
          updateData.fields["Working Style"] = editableRoleDetails["Working Style"];
          break;
        case 'jobDescription':
          updateData.fields["Job Description"] = editableRoleDetails["Job Description"];
          break;
        case 'roleTitle':
          updateData.fields["Role Title"] = editableRoleDetails["Role Title"];
          break;
        case 'jobDescriptionPDF':
          updateData.fields["Job Description PDF"] = editableRoleDetails["Job Description PDF"];
          break;
        default:
          break;
      }
      
      // Update the role data
      const response = await axios.patch("/api/roles/updateRoleData", updateData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      
      console.log("Role updated:", response.data);
      
      // Update the local role details state
      setRoleDetails(prev => ({
        ...prev,
        ...updateData.fields
      }));
      
      // Exit edit mode for this field
      setEditMode(prev => ({
        ...prev,
        [field]: false
      }));
      
    } catch (error) {
      console.error("Error updating role details:", error);
      setError("Failed to update role details. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSendRequest = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem("token");
      
      // For direct requests vs. role-specific requests
      if (selectedRole) {
        // === HANDLE ROLE-SPECIFIC REQUEST ===
        // If a role is selected, update role with contact details
        const roleUpdateData = {
          fields: {
            "Hiring Lead Name": formData.hiringLeadName,
            "Hiring Lead Email Address": formData.hiringLeadEmail,
            "Eye catching information": formData.eyeCatchingInfo,
          },
          roleId: selectedRole.id,
        };
        
        await axios.patch("/api/roles/updateRoleData", roleUpdateData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        
        // Create interview record with role
        const interviewData = {
          fields: {
            Role: [selectedRole.id],
            Candidate: [candidate.id],
            "Interviewer's Name": formData.hiringLeadName,
            "Interviewer's Email": formData.hiringLeadEmail,
            "Match request direct message text": formData.eyeCatchingInfo,
            "Eye Catching Information": formData.eyeCatchingInfo,
            Stage: "Match requested",
            "Match type": "Match Request",
            ...(candidate.most_recent_cohort && candidate.most_recent_cohort.length > 0 && {
              "Matching cohort": candidate.most_recent_cohort[0],
            }),
          },
        };
        
        console.log("Creating interview with data:", interviewData);
        
        const response = await axios.post(
          "/api/interviews/createInterview",
          interviewData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        
        console.log(`Interview created for candidate ${candidate.id}:`, response.data);
      } else {
        // === HANDLE DIRECT REQUEST (NO ROLE) ===
        // Use our new API endpoint that doesn't require a Role field
        const directRequestData = {
          fields: {
            Candidate: [candidate.id],
            "Interviewer's Name": formData.hiringLeadName,
            "Interviewer's Email": formData.hiringLeadEmail,
            "Match request direct message text": formData.eyeCatchingInfo,
            "Eye Catching Information": formData.eyeCatchingInfo,
            ...(candidate.most_recent_cohort && candidate.most_recent_cohort.length > 0 && {
              "Matching cohort": candidate.most_recent_cohort[0],
            }),
          },
        };
        
        console.log("Creating direct request with data:", directRequestData);
        
        const response = await axios.post(
          "/api/interviews/createDirectRequest",
          directRequestData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        
        console.log(`Direct request created for candidate ${candidate.id}:`, response.data);
      }
      
      setShowSuccess(true);
      if (onRequestSent && candidate && selectedRole) {
        onRequestSent(candidate.id, selectedRole.id);
      }
    } catch (error) {
      console.error("Error creating request:", error);
      // Show more detailed error if available from the server
      if (error.response?.data) {
        console.log("Server error details:", error.response.data);
        setError(error.response.data.message || error.response.data.error || "Server error. Please try again.");
      } else {
        setError("Failed to create request. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

    if (!isOpen) return null;

  // Format salary for display
  const formatSalary = (minimum, ideal) => {
    if (!minimum && !ideal) return "Not specified";
    
    // Handle different data formats from Airtable
    const cleanValue = (val) => {
      if (!val) return null;
      // If it's a number, return it directly
      if (typeof val === 'number') return val;
      // If it's already a string with k suffix like "100k", extract just the number
      if (typeof val === 'string' && val.toLowerCase().endsWith('k')) {
        return parseInt(val.toLowerCase().replace('k', '').replace(/[^0-9]/g, ''), 10);
      }
      // Otherwise, try to parse it as a number
      return parseInt(val.toString().replace(/[^0-9]/g, ''), 10);
    };
    
    const min = cleanValue(minimum);
    const max = cleanValue(ideal);
    
    if (min && max) {
      if (min === max) {
        return `£${min}k`;
      } else {
        return `£${min}k - £${max}k`;
      }
    } else if (min) {
      return `£${min}k+`;
    } else if (max) {
      return `Up to £${max}k`;
    }
    
    return "Not specified";
  };

  // Update the isRoleComplete function threshold
  const isRoleComplete = (roleData) => {
    if (!roleData) {
      console.log("DEBUGGING: roleData is null or undefined");
      return false;
    }
    // Define field categories just like in roles2.js
    const fieldLabels = {
      "Role Title": "Role Title",
      "Compensation": "Compensation",
      "Working Style": "Working Style",
      "Hiring Lead Name": "Hiring Lead Name",
      "Hiring Lead Email Address": "Hiring Lead Email Address",
      "Job Description": "Job Description",
      "Job Description PDF": "Job Description PDF",
      "Spotlight": "Video Recording",
      "Equity": "Equity",
      "Specific Experience": "Specific Experience",
      "Hiring Lead Linkedin": "Hiring Lead Linkedin",
      "Interview Process": "Interview Process"
    };
    const mandatoryFields = [
      "Role Title",
      "Compensation",
      "Working Style",
      "Hiring Lead Name",
      "Hiring Lead Email Address"
    ];
    const importantFields = [
      // Either Job Description or Job Description PDF is required
      ["Job Description", "Job Description PDF"],
      "Spotlight"
    ];
    const additionalFields = [
      "Equity",
      "Specific Experience",
      "Hiring Lead Linkedin",
      "Interview Process"
    ];
    // Find missing mandatory fields
    const missingMandatory = mandatoryFields.filter(
      (field) => !roleData[field]
    );
    // Find missing important fields
    let missingImportant = [];
    // For the first important field, either Job Description or Job Description PDF must be present
    if (!roleData["Job Description"] && !roleData["Job Description PDF"]) {
      missingImportant.push("Job Description");
    }
    // For Spotlight
    if (!roleData["Spotlight"]) {
      missingImportant.push(fieldLabels["Spotlight"]);
    }
    // Find missing additional fields
    const missingAdditional = additionalFields.filter(
      (field) => !roleData[field]
    );
    // Count filled fields in each category
    const filledMandatoryFields = mandatoryFields.length - missingMandatory.length;
    const filledImportantFields = 2 - missingImportant.length;
    const filledAdditionalFields = additionalFields.length - missingAdditional.length;
    // Calculate total filled fields and completion percentage
    const totalFilledFields = filledMandatoryFields + filledImportantFields + filledAdditionalFields;
    const completionPercentage = (totalFilledFields / 11) * 100; // Using 11 as denominator like in roles2.js
    // Determine completion status and missing field
    let colorClass = "text-gray-500"; // Default
    // Red: Missing any mandatory field or any important field
    if (filledMandatoryFields < mandatoryFields.length || filledImportantFields === 0) {
      colorClass = "text-red-500";
    }
    // Orange: All mandatory fields are filled but missing one important field
    else if (filledMandatoryFields === mandatoryFields.length && filledImportantFields === 1) {
      colorClass = "text-orange-400";
    }
    // Green: All mandatory and important fields are filled
    else if (filledMandatoryFields === mandatoryFields.length && filledImportantFields === 2) {
      colorClass = "text-green-400";
    }
    // Collect all missing fields for display
    const missingFields = [
      ...missingMandatory.map((f) => fieldLabels[f] || f),
      ...missingImportant,
      ...missingAdditional.map((f) => fieldLabels[f] || f)
    ];
    // Return an enhanced object with all the information
    return {
      isComplete: completionPercentage >= 90, // Changed from 70% to 90%
      completionPercentage,
      colorClass,
      missingFields,
      filledMandatoryFields,
      totalMandatoryFields: mandatoryFields.length,
      filledImportantFields,
      totalImportantFields: 2
    };
  };

  // Determine if modal should be wider
  const roleCompletionDetails = roleDetails ? isRoleComplete(roleDetails) : null;
  const missingFieldsCount = roleCompletionDetails ? roleCompletionDetails.missingFields.length : 0;
  const isWideModal = missingFieldsCount > 3; // Threshold for wider modal

  return createPortal(
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 transition-opacity duration-300"
      style={{ zIndex: 9999 }}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }}
    >
      <div 
        className={`bg-white rounded-xl shadow-xl w-full ${
          isWideModal ? "max-w-3xl" : "max-w-2xl"
        } overflow-hidden relative flex flex-col`}
        style={{ maxHeight: '90vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-5 right-5 p-1.5 rounded-full hover:bg-gray-100 transition-colors"
          style={{ zIndex: 10001 }}
          aria-label="Close modal"
        >
          <X size={18} className="text-gray-500" />
        </button>

        {/* Loading Overlay */}
        {loading && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/80 backdrop-blur-sm transition-opacity duration-300">
            <div className="flex flex-col items-center">
              <img src={LoadingBlack} className="h-12 w-12 animate-spin" alt="Loading"/>
              <p className="mt-4 text-gray-600 font-medium">Processing request...</p>
            </div>
          </div>
        )}

        {/* Success Message */}
        {showSuccess ? (
          <div className="p-8 flex-grow flex items-center justify-center">
            <div className="text-center max-w-md mx-auto">
              <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check size={28} className="text-green-600" />
              </div>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                Interview Request Sent
              </h2>
              <p className="text-gray-600 mb-8">
                We've sent your request to <span className="font-medium">{candidate?.name}</span>. 
             If they accept you'll be introduced directly over email as soon as possible. Fingers crossed!
              </p>
              <button
                className="px-8 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg transition-colors shadow-sm"
                onClick={onClose}
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="px-6 pt-6 pb-4">
              <h2 className="text-xl font-semibold text-gray-900">
                {selectedRole ? "Interview Request" : "Direct Request"}
              </h2>
              <p className="text-gray-500 mt-1">
                Send {selectedRole ? "an interview" : "a direct"} request to {candidate?.name}
              </p>
            </div>
            
            {/* Add error message display in the modal */}
            {error && (
              <div className="mx-6 mb-4 bg-red-50 border border-red-100 text-red-800 px-4 py-3 rounded-lg flex items-start">
                <svg className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <p className="ml-3 text-sm">{error}</p>
              </div>
            )}
            
            {/* Role completion warning - Mobile only (moved from footer) */}
            {selectedRole && isRoleInfoMissing && (
              <div className="md:hidden mx-6 mb-4 bg-amber-50 border border-amber-100 rounded-md px-4 py-3 flex items-start">
                <Info size={16} className="text-amber-500 mt-0.5 flex-shrink-0" />
                <div className="ml-3">
                  <p className="text-sm text-amber-800">
                    Role completion: <span className="font-semibold">{completionPercentage.toFixed(0)}%</span>
                  </p>
                  <p className="text-sm text-amber-800">
                    Complete role information improves candidate response rates
                  </p>
                  {/* Show missing fields if any */}
                  {roleDetails && isRoleInfoMissing && roleCompletionDetails && roleCompletionDetails.missingFields.length > 0 && (
                    <div className="mt-2">
                      <span className="text-sm font-medium text-amber-900">Missing fields:</span>
                      <ul className="list-disc list-inside text-sm text-amber-900 mt-1">
                        {roleCompletionDetails.missingFields.map((field, idx) => (
                          <li key={idx}>{field}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {selectedRole ? (
              // Standard form with role details
              <div className="px-6 overflow-y-auto flex-grow">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Left column - Role info */}
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">Role Summary</h3>
                    
                    {roleDetails && (
                      <div className="space-y-6">
                        {/* Role Title section - editable */}
                        <div>
                          {!editMode.roleTitle ? (
                            <div className="flex items-center">
                              <h4 className="text-lg font-medium text-gray-900 flex-1">
                                {roleDetails["Role Title"]}
                              </h4>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditMode({...editMode, roleTitle: true});
                                }}
                                className="p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                              >
                                <Edit2 size={14} />
                              </button>
                            </div>
                          ) : (
                            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                              <div className="flex justify-between items-center mb-3">
                                <span className="font-medium text-gray-700">Edit Role Title</span>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditMode({...editMode, roleTitle: false});
                                  }}
                                  className="p-1 rounded-full hover:bg-gray-200 text-gray-500"
                                >
                                  <X size={14} />
                                </button>
                              </div>
                              <div className="mb-3">
                                <input
                                  type="text"
                                  value={editableRoleDetails["Role Title"]}
                                  onChange={(e) => handleRoleDetailChange("Role Title", e.target.value)}
                                  placeholder="e.g. Software Engineer"
                                  className="w-full p-2.5 border border-gray-300 rounded-md text-sm"
                                />
                              </div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  saveRoleDetails('roleTitle');
                                }}
                                className="w-full py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md flex items-center justify-center text-sm"
                              >
                                <Save size={14} className="mr-1.5" />
                                Save
                              </button>
                            </div>
                          )}
                        </div>
                        
                        {/* Compensation section - editable */}
                        <div>
                          {!editMode.compensation ? (
                            <div className="flex items-center">
                              <div className="flex-1">
                                <p className="text-sm text-gray-500 mb-1">Compensation</p>
                                <div className="font-medium text-gray-800">
                                  {roleDetails["Compensation"] ? (
                                    roleDetails["Compensation"]
                                  ) : (
                                    <div className="flex items-center text-amber-600">
                                      <Info size={14} className="mr-1" />
                                      <span className="text-sm">Missing information</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditMode({...editMode, compensation: true});
                                }}
                                className="p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                              >
                                <Edit2 size={14} />
                              </button>
                            </div>
                          ) : (
                            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                              <div className="flex justify-between items-center mb-3">
                                <span className="font-medium text-gray-700">Edit Compensation</span>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditMode({...editMode, compensation: false});
                                  }}
                                  className="p-1 rounded-full hover:bg-gray-200 text-gray-500"
                                >
                                  <X size={14} />
                                </button>
                              </div>
                              <div className="mb-3">
                                <input
                                  type="text"
                                  value={editableRoleDetails["Compensation"]}
                                  onChange={(e) => handleRoleDetailChange("Compensation", e.target.value)}
                                  placeholder="e.g. £50k-£70k"
                                  className="w-full p-2.5 border border-gray-300 rounded-md text-sm"
                                />
                              </div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  saveRoleDetails('compensation');
                                }}
                                className="w-full py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md flex items-center justify-center text-sm"
                              >
                                <Save size={14} className="mr-1.5" />
                                Save
                              </button>
                            </div>
                          )}
                        </div>
                        
                        {/* Working Style section - editable */}
                        <div>
                          {!editMode.workingStyle ? (
                            <div className="flex items-center">
                              <div className="flex-1">
                                <p className="text-sm text-gray-500 mb-1">Working Style</p>
                                <div className="font-medium text-gray-800">
                                  {roleDetails["Working Style"] ? (
                                    roleDetails["Working Style"]
                                  ) : (
                                    <div className="flex items-center text-amber-600">
                                      <Info size={14} className="mr-1" />
                                      <span className="text-sm">Missing information</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditMode({...editMode, workingStyle: true});
                                }}
                                className="p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                              >
                                <Edit2 size={14} />
                              </button>
                            </div>
                          ) : (
                            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                              <div className="flex justify-between items-center mb-3">
                                <span className="font-medium text-gray-700">Edit Working Style</span>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditMode({...editMode, workingStyle: false});
                                  }}
                                  className="p-1 rounded-full hover:bg-gray-200 text-gray-500"
                                >
                                  <X size={14} />
                                </button>
                              </div>
                              <div className="mb-3">
                                <input
                                  type="text" 
                                  value={editableRoleDetails["Working Style"]}
                                  onChange={(e) => handleRoleDetailChange("Working Style", e.target.value)}
                                  placeholder="e.g. Hybrid (3 days in office)"
                                  className="w-full p-2.5 border border-gray-300 rounded-md text-sm"
                                />
                                <p className="text-xs text-gray-500 mt-1.5">Examples: Remote, Hybrid, Fully in office</p>
                              </div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  saveRoleDetails('workingStyle');
                                }}
                                className="w-full py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md flex items-center justify-center text-sm"
                              >
                                <Save size={14} className="mr-1.5" />
                                Save
                              </button>
                            </div>
                          )}
                        </div>
                        
                        {/* Job Description section - editable */}
                        {roleDetails["Job Description"] && (
                          <div>
                            {!editMode.jobDescription ? (
                              <div className="flex items-start">
                                <div className="flex-1">
                                  <p className="text-sm text-gray-500 mb-1">Job Description</p>
                                  <div className="bg-gray-50 p-3 rounded-md border border-gray-200 text-sm text-gray-700" style={{maxHeight: '120px', overflowY: 'auto'}}>
                                    {roleDetails["Job Description"] || "No job description provided"}
                                  </div>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditMode({...editMode, jobDescription: true});
                                    }}
                                    className="mt-2 text-sm text-emerald-600 hover:text-emerald-800 flex items-center"
                                  >
                                    <Edit2 size={12} className="mr-1" />
                                    Edit description
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                <div className="flex justify-between items-center mb-3">
                                  <span className="font-medium text-gray-700">Edit Job Description</span>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditMode({...editMode, jobDescription: false});
                                    }}
                                    className="p-1 rounded-full hover:bg-gray-200 text-gray-500"
                                  >
                                    <X size={14} />
                                  </button>
                                </div>
                                <textarea
                                  value={editableRoleDetails["Job Description"]}
                                  onChange={(e) => handleRoleDetailChange("Job Description", e.target.value)}
                                  className="w-full p-2.5 border border-gray-300 rounded-md text-sm mb-3"
                                  rows={4}
                                />
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    saveRoleDetails('jobDescription');
                                  }}
                                  className="w-full py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md flex items-center justify-center text-sm"
                                >
                                  <Save size={14} className="mr-1.5" />
                                  Save
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {roleDetails && roleDetails["Job Description PDF"] && (
                          <div>
                            {!editMode.jobDescriptionPDF ? (
                              <div className="flex items-center justify-between">
                                <a 
                                  href={roleDetails["Job Description PDF"]} 
                                  target="_blank" 
                                  rel="noopener noreferrer" 
                                  className="inline-flex items-center text-sm text-emerald-600 hover:text-emerald-800"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 mr-1">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                    <polyline points="14 2 14 8 20 8"></polyline>
                                    <path d="M12 18v-6"></path>
                                    <path d="M8 15h8"></path>
                                  </svg>
                                  View Job Description PDF
                                </a>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditMode({...editMode, jobDescriptionPDF: true});
                                  }}
                                  className="p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                                >
                                  <Edit2 size={14} />
                                </button>
                              </div>
                            ) : (
                              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                <div className="flex justify-between items-center mb-3">
                                  <span className="font-medium text-gray-700">Edit Job Description PDF URL</span>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditMode({...editMode, jobDescriptionPDF: false});
                                    }}
                                    className="p-1 rounded-full hover:bg-gray-200 text-gray-500"
                                  >
                                    <X size={14} />
                                  </button>
                                </div>
                                <div className="mb-3">
                                  <input
                                    type="text"
                                    value={editableRoleDetails["Job Description PDF"]}
                                    onChange={(e) => handleRoleDetailChange("Job Description PDF", e.target.value)}
                                    placeholder="Enter URL for the job description PDF"
                                    className="w-full p-2.5 border border-gray-300 rounded-md text-sm"
                                  />
                                </div>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    saveRoleDetails('jobDescriptionPDF');
                                  }}
                                  className="w-full py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md flex items-center justify-center text-sm"
                                >
                                  <Save size={14} className="mr-1.5" />
                                  Save
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                        {/** Add 'Edit full role' button if role is complete **/}
                        {selectedRole && !isRoleInfoMissing && (
                          <button
                            type="button"
                            onClick={() => window.open(`/edit-role?roleId=${selectedRole.id}`, '_blank', 'noopener,noreferrer')}
                            className="mt-4 w-full py-2 bg-white border border-emerald-600 text-emerald-700 hover:bg-emerald-50 font-medium rounded-lg flex items-center justify-center gap-2 transition-colors text-sm"
                          >
                            Edit full role
                            <ExternalLink size={16} className="ml-1" />
                          </button>
                        )}
                        {roleDetails && !roleDetails["Job Description PDF"] && !roleDetails["Job Description"] && (
                          !editMode.jobDescriptionPDF ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation(); 
                                setEditMode({...editMode, jobDescriptionPDF: true});
                              }}
                              className="text-sm text-emerald-600 hover:text-emerald-800 flex items-center mt-2"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 mr-1">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                <polyline points="14 2 14 8 20 8"></polyline>
                                <line x1="12" y1="12" x2="12" y2="16"></line>
                                <line x1="10" y1="14" x2="14" y2="14"></line>
                              </svg>
                              Add Job Description PDF URL
                            </button>
                          ) : (
                            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mt-2">
                              <div className="flex justify-between items-center mb-3">
                                <span className="font-medium text-gray-700">Add Job Description PDF URL</span>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditMode({...editMode, jobDescriptionPDF: false});
                                  }}
                                  className="p-1 rounded-full hover:bg-gray-200 text-gray-500"
                                >
                                  <X size={14} />
                                </button>
                              </div>
                              <div className="mb-3">
                                <input
                                  type="text"
                                  value={editableRoleDetails["Job Description PDF"]}
                                  onChange={(e) => handleRoleDetailChange("Job Description PDF", e.target.value)}
                                  placeholder="Enter URL for the job description PDF"
                                  className="w-full p-2.5 border border-gray-300 rounded-md text-sm"
                                />
                              </div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  saveRoleDetails('jobDescriptionPDF');
                                }}
                                className="w-full py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md flex items-center justify-center text-sm"
                              >
                                <Save size={14} className="mr-1.5" />
                                Save
                              </button>
                            </div>
                          )
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Right column - Form */}
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">Contact Information</h3>
                    
                    <div className="space-y-5">
                      <div>
                        <label htmlFor="hiringLeadName" className="block text-sm font-medium text-gray-700 mb-1">
                          Hiring Lead Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          id="hiringLeadName"
                          name="hiringLeadName"
                          value={formData.hiringLeadName}
                          onChange={handleFormChange}
                          placeholder="Enter the name of the hiring manager"
                          className="w-full p-2.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                          required
                        />
                      </div>
                      
                      <div>
                        <label htmlFor="hiringLeadEmail" className="block text-sm font-medium text-gray-700 mb-1">
                          Hiring Lead Email <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          id="hiringLeadEmail"
                          name="hiringLeadEmail"
                          value={formData.hiringLeadEmail}
                          onChange={handleFormChange}
                          placeholder="Enter the email of the hiring manager"
                          className="w-full p-2.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                          required
                        />
                      </div>
                      
                      <div>
                        <label htmlFor="eyeCatchingInfo" className="block text-sm font-medium text-gray-700 mb-1">
                          Personal Message
                        </label>
                        <textarea
                          id="eyeCatchingInfo"
                          name="eyeCatchingInfo"
                          value={formData.eyeCatchingInfo}
                          onChange={handleFormChange}
                          placeholder={`e.g. Hi ${candidate?.name || 'there'}, really impressed by your profile - especially your experience driving customer engagement at Sky. Would love to chat about a couple of upcoming roles with you!`}
                          className="w-full p-2.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                          rows={4}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              // Simplified form for "Request without a role"
              <div className="px-6 overflow-y-auto flex-grow">
                <div className="max-w-lg mx-auto space-y-5">
                  <div>
                    <label htmlFor="hiringLeadName" className="block text-sm font-medium text-gray-700 mb-1">
                      Your Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="hiringLeadName"
                      name="hiringLeadName"
                      value={formData.hiringLeadName}
                      onChange={handleFormChange}
                      placeholder="Enter your name"
                      className="w-full p-2.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      required
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="hiringLeadEmail" className="block text-sm font-medium text-gray-700 mb-1">
                      Your Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="hiringLeadEmail"
                      name="hiringLeadEmail"
                      value={formData.hiringLeadEmail}
                      onChange={handleFormChange}
                      placeholder="Enter your email address"
                      className="w-full p-2.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      required
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="eyeCatchingInfo" className="block text-sm font-medium text-gray-700 mb-1">
                      Your Message <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="eyeCatchingInfo"
                      name="eyeCatchingInfo"
                      value={formData.eyeCatchingInfo}
                      onChange={handleFormChange}
                      placeholder="Please include details about the role or why you'd like to connect"
                      className="w-full p-2.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      rows={5}
                      required
                    />
                  </div>
                </div>
              </div>
            )}
            
            {/* Footer with button */}
            <div className="px-6 py-5 mt-2">
              {/* Role completion warning - Desktop only (mobile version is at top) */}
              {selectedRole && isRoleInfoMissing && (
                <div className="hidden md:block bg-amber-50 border border-amber-100 rounded-md px-4 py-3 mb-4 flex items-start">
                  <Info size={16} className="text-amber-500 mt-0.5 flex-shrink-0" />
                  <div className="ml-3">
                    <p className="text-sm text-amber-800">
                      Role completion: <span className="font-semibold">{completionPercentage.toFixed(0)}%</span>
                    </p>
                    <p className="text-sm text-amber-800">
                      Complete role information improves candidate response rates
                    </p>
                    {/* Show missing fields if any */}
                    {roleDetails && isRoleInfoMissing && roleCompletionDetails && roleCompletionDetails.missingFields.length > 0 && (
                      <div className="mt-2">
                        <span className="text-sm font-medium text-amber-900">Missing fields:</span>
                        <ul className="list-disc list-inside text-sm text-amber-900 mt-1">
                          {roleCompletionDetails.missingFields.map((field, idx) => (
                            <li key={idx}>{field}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {selectedRole && isRoleInfoMissing ? (
                <div className="flex space-x-3">
                  <button
                    onClick={() => window.open(`/edit-role?roleId=${selectedRole.id}`, '_blank', 'noopener,noreferrer')}
                    className="flex-1 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    Complete Role
                    <ExternalLink size={16} className="ml-2" />
                  </button>
                  <button
                    onClick={handleSendRequest}
                    disabled={!formData.hiringLeadName || !formData.hiringLeadEmail}
                    className="flex-1 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Send Anyway
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleSendRequest}
                  disabled={!formData.hiringLeadName || !formData.hiringLeadEmail || (selectedRole && !roleDetails)}
                  className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {selectedRole ? "Send Interview Request" : "Send Direct Request"}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

export default RoleMatchRequestModal; 